import {
  CwsAlert,
  CwsGridCol,
  CwsGridRow,
  CwsH5,
  CwsInput,
  CwsP,
  CwsSelect,
  CwsSelectOption,
  CwsLabel,
} from '@components/cws'
import type { FC, ChangeEvent } from 'react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslate } from '@hooks/useTranslation'
import { useCartState } from '@components/cart/State/state'
import useSWR from 'swr'
import type { AxiosResponse } from 'axios'
import axios from 'axios'
import type { PickupPoint } from '@api/ecw/models/PickupPoint'
import AddressInputWithSearch from '@components/cart/delivery/DeliveryMethods/AddressInputWithSearch'
import CourierList from '@components/cart/delivery/DeliveryMethods/CourierList'
import type { KonakartCarrierCode } from '@model/order/ShippingQuote'
import type { DeliveryMethod, DeliveryMethodWithQuote } from '@api/ecw/models/DeliveryMethod'
import HomeDelivery from '@components/cart/delivery/DeliveryMain/HomeDelivery'
import type { OptionsSelect } from '@ui/components/SelectSearch/SelectSearch'
import type { PickupPointRequest } from '@api/ecw/models/PickupPoint'
import { COUNTRY_ISO2 } from '@model/locales'
import { validations } from '@utils/order'
import { localStorageHelper } from '@utils/localStorageHelper'
import { alphabetOption, sortStringsByDetectedLanguage } from '@utils/sortStringsByDetectedLanguage'
import { useDebouncedValue } from '@hooks/useDebounce'

export const emptyPickUpPoint = {
  name: '',
  address1: '',
  id: '',
}

interface DeliverySubtypeProps {
  codesMap: Record<KonakartCarrierCode, DeliveryMethodWithQuote>
  deliveryMethods?: DeliveryMethod[]
  fromReturn?: boolean
  loading?: boolean
}

export const DeliverySubtype: FC<DeliverySubtypeProps> = ({ codesMap, deliveryMethods, fromReturn, loading }) => {
  const { translate } = useTranslate()
  const {
    dispatch,
    state: { deliveryMethodsByCountry, deliveryCountry, selectedPickUpPointId, order, homeDeliveryAddress },
  } = useCartState()
  const hasAlcohol = useMemo(() => !!order?.orderProducts.find((item) => item.custom2), [order])
  const [pointSearchValue, setPointSearchValue] = useState<string>('')
  const selectPickUpPoint = useCallback(
    (val: string) => dispatch({ type: 'selectPickUpPointId', payload: val }),
    [dispatch]
  )

  const selectedDeliveryMethod = useMemo(
    () => deliveryMethodsByCountry[deliveryCountry],
    [deliveryMethodsByCountry, deliveryCountry]
  )

  const [zip, setZip] = useState<string>('')
  const zipValue = useDebouncedValue(zip, 2000)

  useEffect(() => {
    setZip('')
    setAddressString('')
  }, [deliveryCountry, setZip])

  const shouldShowAndCheckZipCode = useMemo(
    () =>
      (deliveryCountry === 'Finland' || deliveryCountry === 'Sweden') &&
      !selectedDeliveryMethod?.express &&
      selectedDeliveryMethod?.parcelMachine,
    [deliveryCountry, selectedDeliveryMethod]
  )

  const {
    data: response,
    isValidating,
    isLoading,
  } = useSWR<AxiosResponse<PickupPoint[]>>(
    deliveryMethodsByCountry[deliveryCountry] &&
      `puPoints-${deliveryMethodsByCountry[deliveryCountry]?.konakartCarrierCode}-${zipValue}`,
    () =>
      axios.post<PickupPointRequest, AxiosResponse<PickupPoint[]>>(`/api/pickup-points`, {
        carrierCode: deliveryMethodsByCountry[deliveryCountry]?.konakartCarrierCode,
        noCache: true,
        hasAlcohol: hasAlcohol,
        countryCode: COUNTRY_ISO2[deliveryCountry],
        ...(zipValue && { zip: zipValue }),
      } as PickupPointRequest)
  )

  const zipListTallinkTakso = useMemo(() => {
    if (deliveryMethodsByCountry[deliveryCountry]?.konakartCarrierCode === 'EKSPRESS' && response?.data) {
      return response?.data.map((item) => item.zipCode)
    }
    return []
  }, [response?.data, deliveryMethodsByCountry[deliveryCountry]?.konakartCarrierCode])

  useEffect(() => {
    if (response && response?.data?.length) {
      const deliveryPoint = response.data.find((ps) => ps.id === selectedPickUpPointId)
      dispatch({ type: 'setSelectDeliveryPoint', payload: deliveryPoint || null })
    }
  }, [selectedPickUpPointId, response, dispatch])

  const showHomeDeliveryForm = useMemo(() => {
    if (selectedDeliveryMethod?.konakartCarrierCode === 'BUDBEESPFI') return true
    const isTaxi = selectedDeliveryMethod?.konakartCarrierCode === 'EKSPRESS'
    return !selectedDeliveryMethod?.parcelMachine && !selectedDeliveryMethod?.express
  }, [selectedDeliveryMethod])

  const optionsPickUpPoint: OptionsSelect[] = useMemo(() => {
    if (!response?.data) return []
    const res = response.data.map<OptionsSelect>((item) => ({
      id: item.id,
      name: `${item.name || ''} (${item.address1})`,
    }))
    if (alphabetOption[deliveryCountry]) return sortStringsByDetectedLanguage(res, alphabetOption[deliveryCountry])
    return res
  }, [response?.data, deliveryCountry])
  const parcelMachineLabel = useMemo(() => {
    if (selectedDeliveryMethod?.express) return translate('w2.cart.parcel.pickup.point')
    return translate('basket.courier.selection.machine.filter.label')
  }, [translate, selectedDeliveryMethod])

  const [addressString, setAddressString] = useState<string>('')
  const zipHistory = localStorageHelper.get('zipValue')
  useEffect(() => {
    if (homeDeliveryAddress?.streetAddress && homeDeliveryAddress.city && homeDeliveryAddress.zip) {
      setAddressString(`${homeDeliveryAddress.streetAddress},${homeDeliveryAddress.city}`)
      setZip(homeDeliveryAddress.zip)
      return
    }
    if (zipHistory) {
      setZip(zipHistory)
    }
  }, [])

  const zipPattern = validations.zip
  if (!deliveryMethodsByCountry[deliveryCountry] || !response) return null

  const handleChangePickUpPoint = (event: { value: string; type: string }) => {
    const { value, type } = event

    if (type === 'option') {
      selectPickUpPoint(value)
      const searchValue = optionsPickUpPoint.find((item) => item.id === value)
      setPointSearchValue(searchValue?.name || '')
    } else {
      setPointSearchValue(value)
    }
  }
  return (
    <>
      <CwsGridRow noGutter>
        <CwsH5 className="cws-py-12-lg" weight="500">
          {showHomeDeliveryForm ? translate('basket.shipping.address.header') : parcelMachineLabel}
        </CwsH5>
      </CwsGridRow>
      <CwsGridRow noGutter className="w-full">
        <CwsGridCol col="12">
          {shouldShowAndCheckZipCode && deliveryCountry === 'Sweden' && (
            <AddressInputWithSearch
              zipValue={zip}
              onZipChange={setZip}
              addressString={addressString}
              setAddressString={setAddressString}
            />
          )}

          {shouldShowAndCheckZipCode && deliveryCountry !== 'Sweden' && (
            <CwsGridRow noGutter className="w-full">
              <CwsGridCol col="6" className="cws-mb-xs">
                <CwsLabel>{translate('basket.shipping.address.zip')}</CwsLabel>
                <CwsInput
                  id="check-delivery-zip-filter"
                  name="zip"
                  onChange={(value: string) => {
                    selectPickUpPoint('')
                    const zipValue = value
                    setZip(value)
                    localStorageHelper.set('zipValue', zipValue)
                    if (zipValue.match(zipPattern)) {
                    }
                  }}
                  value={zip}
                  invalid={!!zip && (!zip.match(zipPattern) || !Boolean(optionsPickUpPoint.length))}
                />

                {!!zip && (!zip.match(zipPattern) || !Boolean(optionsPickUpPoint.length)) ? (
                  <CwsP color="error">{translate('w2.zip.carrierCode.validation.message')}</CwsP>
                ) : (
                  <CwsAlert variant="inline" className="cws-justify-content-center" type="info">
                    <CwsP size="small">{translate('basket.courier.zip.help')}</CwsP>
                  </CwsAlert>
                )}
                {/*<div className="flex flex-row min-h-[2px] min-w-[35px] lg:min-w-[106px] lg:mb-4 mx-3 lg:mx-2"></div>*/}
              </CwsGridCol>
            </CwsGridRow>
          )}
          <CwsGridRow>
            <CwsGridCol col="12" className="cws-mb-xs">
              <CourierList
                codesMap={codesMap}
                deliveryMethodsForReturnOrder={deliveryMethods}
                // disabled={isLoading}
                disabled={(shouldShowAndCheckZipCode && !Boolean(zip)) || isLoading || loading}
              />
            </CwsGridCol>

            {selectedDeliveryMethod?.parcelMachine && (
              <CwsGridCol col="12">
                <CwsSelect
                  data-testid="selectedDeliveryMethod"
                  placeholder={
                    selectedDeliveryMethod.express
                      ? translate('w2.basket.express.pick.terminal')
                      : translate('basket.pick.terminal')
                  }
                  onChange={handleChangePickUpPoint}
                  value={pointSearchValue}
                  variant="autocomplete"
                  disabled={
                    !Boolean(optionsPickUpPoint.length) ||
                    (shouldShowAndCheckZipCode ? !Boolean(zip) : isValidating) ||
                    isLoading ||
                    loading
                  }
                >
                  {optionsPickUpPoint.map(({ id, name }) => (
                    <CwsSelectOption key={id} title={name} value={id} />
                  ))}
                </CwsSelect>
              </CwsGridCol>
            )}
          </CwsGridRow>
          {showHomeDeliveryForm && (
            <HomeDelivery
              fromReturn={fromReturn}
              withoutZip={selectedDeliveryMethod?.konakartCarrierCode === 'BUDBEESPFI'}
            />
          )}
          {selectedDeliveryMethod?.konakartCarrierCode === 'EKSPRESS' && (
            <HomeDelivery zipListTallinkTakso={zipListTallinkTakso} fromReturn={fromReturn} />
          )}
        </CwsGridCol>
      </CwsGridRow>
    </>
  )
}
//https://ci3.googleusercontent.com/meips/ADKq_NYeHvYG-Wr-3onx7Zlqqm86KTcv0Afn5En5fzu5nRlvl7IxarnXgcF8rW-NrC4RyFlj-pqVNg5j4lG0n9Y9S9iDUe-5rCuET4F8m9Q9GkFCNjlamuj9TTNmWX_aDFcVfIrddygLvoDpEdH2pthqH1Bi_GFfAwYn9iwv7P3NQTPC68OlGPcU1i6Itadahg-m1jT3Vt40itNSHfE_3Iu_O3JP2iSs_Z1okutE_rmRaA5BwOHVAw=s0-d-e1-ft#https://dev-shopping.fleet.zone/_next/image?w=640&amp;q=50&amp;url=https://dev-konakart.fleet.zone/images/prod/5/3/B/5/53B5CE0B-42EE-45BD-96E5-51491E94AD62_1_big.png
